import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth, can } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pencil, Trash2, Plus, Upload, FileText, Download, RefreshCw } from "lucide-react";
import PropertyDialog from "@/components/PropertyDialog";
import TransactionDialog from "@/components/TransactionDialog";
import MaintenanceDialog from "@/components/MaintenanceDialog";
import ContractDialog from "@/components/ContractDialog";
import RenewContractDialog from "@/components/RenewContractDialog";
import PaymentCalendar from "@/components/PaymentCalendar";
import PropertyTimeline from "@/components/PropertyTimeline";
import RecurringExpensesSection from "@/components/RecurringExpensesSection";
import PropertyYieldCard from "@/components/PropertyYieldCard";
import { CalendarClock } from "lucide-react";
import { formatUSD, formatMoney } from "@/components/KPICard";
import { formatDMY, formatDMYTime } from "@/lib/dates";
import { API } from "@/lib/api";
import { PROPERTIES } from "@/constants/testIds";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [txs, setTxs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [allProps, setAllProps] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [providers, setProviders] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [payments, setPayments] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [txOpen, setTxOpen] = useState(false);
  const [mntOpen, setMntOpen] = useState(false);
  const [ctrOpen, setCtrOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [renewingContract, setRenewingContract] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [audit, setAudit] = useState([]);

  const load = async () => {
    try {
      const [p, tx, dc, ap, mn, pr, ct, tn, py] = await Promise.all([
        api.get(`/properties/${id}`),
        api.get(`/transactions?property_id=${id}`),
        api.get(`/documents?property_id=${id}`),
        api.get("/properties"),
        api.get(`/maintenance?property_id=${id}`),
        api.get("/providers"),
        api.get(`/contracts?property_id=${id}`),
        api.get("/tenants"),
        api.get(`/payments?property_id=${id}`),
      ]);
      setProperty(p.data);
      setTxs(tx.data);
      setDocs(dc.data);
      setAllProps(ap.data);
      setMaintenance(mn.data);
      setProviders(pr.data);
      setContracts(ct.data);
      setTenants(tn.data);
      setPayments(py.data);
      if (can(user, "viewAudit")) {
        const a = await api.get("/audit?limit=100");
        setAudit(a.data.filter((x) => x.resource_id === id));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, [id]);

  const totals = useMemo(() => {
    let income = 0, expense = 0;
    for (const t of txs) {
      if (t.kind === "income") income += t.amount_usd;
      else expense += t.amount_usd;
    }
    return { income, expense, net: income - expense };
  }, [txs]);

  const handleDelete = async () => {
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Propiedad eliminada");
      nav("/properties");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("property_id", id);
    fd.append("kind", "contract");
    setUploading(true);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento subido");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const downloadDoc = async (docId, filename) => {
    const resp = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!property) return <div className="text-muted-foreground text-sm">Cargando…</div>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/properties" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex gap-4 items-start">
          <div
            className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg bg-secondary overflow-hidden shrink-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${property.photo_gridfs_id ? `${API}/properties/${property.id}/photo?v=${property.photo_gridfs_id}` : (property.photo_url || '')})` }}
          />
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{property.name}</h1>
            <div className="text-sm text-muted-foreground mt-1">{property.address}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {property.company} · {property.country} · {property.currency}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {can(user, "manageProperties") && (
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid={PROPERTIES.editBtn}>
              <Pencil className="w-4 h-4 mr-1" /> Editar
            </Button>
          )}
          {can(user, "deleteProperty") && (
            <Button variant="outline" onClick={() => setConfirmDel(true)} data-testid={PROPERTIES.deleteBtn} className="text-rose-500 hover:text-rose-500">
              <Trash2 className="w-4 h-4 mr-1" /> Eliminar
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="financial" data-testid={PROPERTIES.detailTabs}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="financial">Financiero</TabsTrigger>
          <TabsTrigger value="contracts">Contratos</TabsTrigger>
          <TabsTrigger value="maintenance">Mantenimiento</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="documents">Documentos</TabsTrigger>
          <TabsTrigger value="info">Información</TabsTrigger>
          {can(user, "viewAudit") && <TabsTrigger value="audit">Auditoría</TabsTrigger>}
        </TabsList>

        <TabsContent value="financial" className="mt-6 space-y-4">
          <PropertyYieldCard propertyId={id} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-border/60 bg-card p-5">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Ingresos USD</div>
              <div className="text-2xl font-heading font-semibold text-emerald-500 num">{formatUSD(totals.income)}</div>
            </div>
            <div className="rounded-xl border border-border/60 bg-card p-5">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Egresos USD</div>
              <div className="text-2xl font-heading font-semibold text-rose-500 num">{formatUSD(totals.expense)}</div>
            </div>
            <div className="rounded-xl border border-border/60 bg-card p-5">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Neto USD</div>
              <div className={`text-2xl font-heading font-semibold num ${totals.net >= 0 ? "text-emerald-500" : "text-rose-500"}`}>{formatUSD(totals.net)}</div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl">Movimientos</h2>
            {can(user, "manageTransactions") && (
              <Button onClick={() => setTxOpen(true)} className="rounded-full" data-testid="property-tx-add">
                <Plus className="w-4 h-4 mr-1" /> Registrar movimiento
              </Button>
            )}
          </div>
          <div className="rounded-xl border border-border/60 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Fecha</th>
                  <th className="px-3 py-2 font-medium">Tipo</th>
                  <th className="px-3 py-2 font-medium">Categoría</th>
                  <th className="px-3 py-2 font-medium text-right">Monto</th>
                  <th className="px-3 py-2 font-medium text-right">USD</th>
                </tr>
              </thead>
              <tbody>
                {txs.map((t) => (
                  <tr key={t.id} className="border-t border-border/50 hover:bg-secondary/40">
                    <td className="px-3 py-2 num">{formatDMY(t.date)}</td>
                    <td className="px-3 py-2">
                      <span className={t.kind === "income" ? "text-emerald-500" : "text-rose-500"}>
                        {t.kind === "income" ? "Ingreso" : "Gasto"}
                      </span>
                    </td>
                    <td className="px-3 py-2 capitalize">{t.category}</td>
                    <td className="px-3 py-2 text-right num">{formatMoney(t.amount, t.currency)}</td>
                    <td className="px-3 py-2 text-right num font-semibold">{formatUSD(t.amount_usd)}</td>
                  </tr>
                ))}
                {!txs.length && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Sin movimientos.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="contracts" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl">Contratos e inquilinos</h2>
            {can(user, "manageProperties") && (
              <Button onClick={() => { setEditingContract(null); setCtrOpen(true); }} className="rounded-full" data-testid="property-contract-add">
                <Plus className="w-4 h-4 mr-1" /> Nuevo contrato
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {contracts.map((c) => {
              const tn = tenants.find((t) => t.id === c.tenant_id);
              return (
                <div key={c.id} className="rounded-xl border border-border/60 bg-card p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{tn?.name || "Inquilino"}</div>
                    <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <CalendarClock className="w-3 h-3" />
                      {formatDMY(c.start_date)} → {formatDMY(c.end_date)}
                      {c.adjustment_pct > 0 && ` · ajuste ${c.adjustment_pct}% cada ${c.adjustment_months} meses`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-lg font-semibold num">{formatMoney(c.monthly_amount, c.currency)}</div>
                    <div className="text-[11px] text-muted-foreground">/ mes</div>
                  </div>
                  {can(user, "manageProperties") && (
                    <div className="flex gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setEditingContract(c); setCtrOpen(true); }}
                        data-testid={`contract-edit-${c.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-emerald-500 hover:text-emerald-500 border-emerald-500/40"
                        onClick={() => setRenewingContract(c)}
                        data-testid={`contract-renew-${c.id}`}
                        title="Renovar contrato 1-click"
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Renovar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-rose-500 hover:text-rose-500"
                        onClick={async () => {
                          const msg = `¿Eliminar contrato de "${property?.name || 'esta propiedad'}"?\n\nEsto también borrará TODOS los pagos asociados (pendientes y ya cobrados) y las transacciones de ingreso generadas.\n\nEsta acción no se puede deshacer.`;
                          if (!window.confirm(msg)) return;
                          try {
                            await api.delete(`/contracts/${c.id}`);
                            toast.success("Contrato y pagos eliminados");
                            load();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || e.message);
                          }
                        }}
                        data-testid={`contract-delete-${c.id}`}
                        title="Eliminar contrato y todos sus pagos"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            {!contracts.length && (
              <div className="rounded-xl border border-border/60 bg-card p-8 text-center text-muted-foreground text-sm">
                Sin contratos activos.
              </div>
            )}
          </div>
          <PaymentCalendar payments={payments} onChanged={load} canEdit={can(user, "manageProperties")} />

          <RecurringExpensesSection propertyId={id} propertyCurrency={property?.currency} />
        </TabsContent>

        <TabsContent value="maintenance" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl">Mantenimiento e histórico</h2>
            {can(user, "manageProperties") && (
              <Button onClick={() => setMntOpen(true)} className="rounded-full" data-testid="property-maintenance-add">
                <Plus className="w-4 h-4 mr-1" /> Nuevo mantenimiento
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {maintenance.map((m) => (
              <Link
                to="/maintenance"
                key={m.id}
                className="block rounded-lg border border-border/60 bg-card p-4 hover:border-foreground/30 transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{m.title}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {m.type} · {m.status} · {formatDMY(m.completed_date || m.scheduled_date) !== "-" ? formatDMY(m.completed_date || m.scheduled_date) : "sin fecha"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold num">{formatUSD(m.cost_usd || 0)}</div>
                    <div className="text-[11px] text-muted-foreground num">{m.cost} {m.currency}</div>
                  </div>
                </div>
              </Link>
            ))}
            {!maintenance.length && (
              <div className="rounded-xl border border-border/60 bg-card p-8 text-center text-muted-foreground text-sm">
                Sin registros de mantenimiento.
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <PropertyTimeline propertyId={id} />
        </TabsContent>

        <TabsContent value="documents" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl">Contratos y documentos</h2>
            {can(user, "manageProperties") && (
              <label className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-border/60 hover:border-foreground/30 px-4 py-2 text-sm">
                <Upload className="w-4 h-4" /> {uploading ? "Subiendo…" : "Subir archivo"}
                <input type="file" onChange={handleUpload} className="hidden" data-testid="property-doc-upload" />
              </label>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {docs.map((d) => (
              <div key={d.id} className="rounded-lg border border-border/60 bg-card p-4 flex items-center gap-3">
                <FileText className="w-5 h-5 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{d.filename}</div>
                  <div className="text-xs text-muted-foreground">{(d.size / 1024).toFixed(1)} KB · {d.kind}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => downloadDoc(d.id, d.filename)}>
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {!docs.length && (
              <div className="col-span-full text-center py-10 text-muted-foreground">Sin documentos.</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="info" className="mt-6">
          <div className="rounded-xl border border-border/60 bg-card p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Estado</div><div className="mt-1">{property.status}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Tipo</div><div className="mt-1 capitalize">{property.property_type}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Valor</div><div className="mt-1 num">{formatMoney(property.value, property.currency)}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Ciudad</div><div className="mt-1">{property.city || "-"}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Coordenadas</div><div className="mt-1 num">{property.latitude ?? "-"}, {property.longitude ?? "-"}</div></div>
            <div className="sm:col-span-2"><div className="text-xs uppercase tracking-widest text-muted-foreground">Notas</div><div className="mt-1 whitespace-pre-wrap">{property.notes || "-"}</div></div>
          </div>
        </TabsContent>

        {can(user, "viewAudit") && (
          <TabsContent value="audit" className="mt-6">
            <div className="rounded-xl border border-border/60 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60"><tr className="text-left"><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Usuario</th><th className="px-3 py-2">Acción</th><th className="px-3 py-2">Recurso</th></tr></thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id} className="border-t border-border/50">
                      <td className="px-3 py-2 num text-xs">{formatDMYTime(a.created_at)}</td>
                      <td className="px-3 py-2">{a.user_email}</td>
                      <td className="px-3 py-2 capitalize">{a.action}</td>
                      <td className="px-3 py-2">{a.resource}</td>
                    </tr>
                  ))}
                  {!audit.length && <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">Sin registros.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <PropertyDialog open={editOpen} onOpenChange={setEditOpen} property={property} onSaved={load} />
      <TransactionDialog open={txOpen} onOpenChange={setTxOpen} properties={allProps} defaultPropertyId={id} onSaved={load} />
      <MaintenanceDialog open={mntOpen} onOpenChange={setMntOpen} properties={allProps} providers={providers} defaultPropertyId={id} onSaved={load} />
      <ContractDialog
        open={ctrOpen}
        onOpenChange={(v) => { setCtrOpen(v); if (!v) setEditingContract(null); }}
        properties={allProps}
        tenants={tenants}
        contract={editingContract}
        onSaved={load}
      />
      <RenewContractDialog
        open={!!renewingContract}
        onOpenChange={(v) => !v && setRenewingContract(null)}
        contract={renewingContract}
        onRenewed={load}
      />

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar propiedad?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán también todas las transacciones y alertas asociadas. Esta acción no puede deshacerse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-500 hover:bg-rose-600">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
